import type { PublicUserState } from '@casino/shared';
import { audio } from '../audio/AudioManager.js';
import { store, type AppState } from '../state/store.js';
import { animate, countTo, formatChips, formatClock, isReducedMotion } from './anim.js';
import { el, icon, ICONS } from './dom.js';
import { sessionGuard } from './sessionGuard.js';
import { getSettings, onSettingsChange, openSettings, toggleMute } from './settings.js';

/**
 * Bandeau joueur : avatar, pseudo, solde, compteur de session.
 *
 * Le solde affiche ici est toujours celui de l'etat autoritaire, c'est-a-dire
 * la derniere reponse de l'API. Le comptage anime ne fait que rendre la
 * transition lisible entre deux valeurs deja decidees par le serveur.
 */

export interface Hud {
  readonly root: HTMLElement;
  readonly destroy: () => void;
}

function initials(username: string): string {
  const cleaned = username.trim();
  if (cleaned.length === 0) return '?';
  const parts = cleaned.split(/\s+/).filter((part) => part.length > 0);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}

/**
 * Ligne de statut du HUD. Format impose par la recette :
 * « Connecté en tant que <pseudo>, solde 1000 ».
 */
function statusLine(user: PublicUserState): string {
  return `Connecté en tant que ${user.username}, solde ${formatChips(user.balance)}`;
}

export function createHud(): Hud {
  const avatarSlot = el('div', { class: 'hud__avatar' });
  const nameNode = el('p', { class: 'hud__name', text: '—' });
  const statusNode = el('p', {
    class: 'hud__status',
    attrs: { 'aria-live': 'polite' },
    text: 'Chargement du profil…',
  });

  const balanceValue = el('span', { class: 'hud__balance-value', text: '—' });
  const balanceBlock = el('div', { class: 'hud__balance' }, [
    el('span', { class: 'hud__balance-label', text: 'Jetons' }),
    el('span', { class: 'hud__balance-figure' }, [
      balanceValue,
      el('span', { class: 'hud__balance-unit', text: 'jetons virtuels' }),
    ]),
  ]);

  const sessionValue = el('span', { class: 'hud__session-value', text: '00:00:00' });
  const sessionBlock = el(
    'div',
    { class: 'hud__session', attrs: { title: 'Durée de la session en cours' } },
    [
      icon(ICONS.clock, { size: 15 }),
      el('span', { class: 'hud__session-label', text: 'Session' }),
      sessionValue,
    ],
  );

  const muteButton = el('button', {
    class: 'icon-button',
    attrs: { type: 'button', 'aria-label': 'Couper le son' },
    on: {
      click: () => {
        toggleMute();
        renderMute();
      },
    },
  });

  const settingsButton = el(
    'button',
    {
      class: 'icon-button',
      attrs: { type: 'button', 'aria-label': 'Paramètres' },
      on: {
        click: () => {
          audio.play('click');
          openSettings();
        },
      },
    },
    [icon(ICONS.settings, { size: 18 })],
  );

  function renderMute(): void {
    const muted = getSettings().muted;
    muteButton.replaceChildren(icon(muted ? ICONS.muted : ICONS.volume, { size: 18 }));
    muteButton.setAttribute('aria-label', muted ? 'Rétablir le son' : 'Couper le son');
    muteButton.dataset['on'] = muted ? 'false' : 'true';
  }
  renderMute();

  const root = el('header', { class: 'hud', attrs: { role: 'banner' } }, [
    el('div', { class: 'hud__identity' }, [
      avatarSlot,
      el('div', { class: 'hud__identity-text' }, [nameNode, statusNode]),
    ]),
    el('div', { class: 'hud__meters' }, [balanceBlock, sessionBlock]),
    el('div', { class: 'hud__actions' }, [muteButton, settingsButton]),
  ]);

  // --- avatar -------------------------------------------------------------
  let avatarKey = '';
  function renderAvatar(user: PublicUserState): void {
    const key = user.avatarUrl ?? `initials:${user.username}`;
    if (key === avatarKey) return;
    avatarKey = key;

    const monogram = el('span', { class: 'hud__monogram', text: initials(user.username) });
    avatarSlot.replaceChildren(monogram);

    if (user.avatarUrl === null) return;
    // L'image peut etre refusee par la CSP de l'iframe selon l'hote : dans ce
    // cas on garde simplement le monogramme, sans erreur visible.
    const image = el('img', {
      class: 'hud__avatar-img',
      attrs: {
        src: user.avatarUrl,
        alt: '',
        decoding: 'async',
        loading: 'lazy',
        referrerpolicy: 'no-referrer',
      },
      on: {
        load: () => {
          avatarSlot.replaceChildren(image);
          animate(image, [{ opacity: 0 }, { opacity: 1 }], { duration: 200 });
        },
      },
    });
  }

  // --- solde --------------------------------------------------------------
  let shownBalance: number | null = null;
  let cancelCount: (() => void) | null = null;

  function renderBalance(user: PublicUserState): void {
    const target = user.balance;
    if (shownBalance === target) return;

    const previous = shownBalance;
    cancelCount?.();

    if (previous === null) {
      balanceValue.textContent = formatChips(target);
      shownBalance = target;
      return;
    }

    const rising = target > previous;
    balanceBlock.dataset['delta'] = rising ? 'up' : 'down';
    if (rising) audio.play('coin');

    if (!isReducedMotion()) {
      animate(
        balanceValue,
        rising
          ? [{ transform: 'translateY(3px)' }, { transform: 'none' }]
          : [{ transform: 'translateY(-3px)' }, { transform: 'none' }],
        { duration: 220 },
      );
    }

    cancelCount = countTo(balanceValue, previous, target, {
      onDone: () => {
        window.setTimeout(() => {
          delete balanceBlock.dataset['delta'];
        }, 700);
      },
    });
    shownBalance = target;
  }

  function render(state: AppState): void {
    const user = state.user;
    if (!user) return;
    nameNode.textContent = user.username;
    statusNode.textContent = statusLine(user);
    renderAvatar(user);
    renderBalance(user);
    root.dataset['live'] = state.live ? 'true' : 'false';
  }

  const unsubscribeStore = store.subscribe(render);
  const unsubscribeSession = sessionGuard.subscribe((elapsed) => {
    sessionValue.textContent = formatClock(elapsed);
  });
  const unsubscribeSettings = onSettingsChange(() => renderMute());

  return {
    root,
    destroy: () => {
      cancelCount?.();
      unsubscribeStore();
      unsubscribeSession();
      unsubscribeSettings();
    },
  };
}
